import { motion } from "framer-motion";
import PageHeader from "@/components/PageHeader";
import { useSeo } from "@/lib/seo";

const SIGNS = [
  {
    id: "conditions-of-use",
    chip: "Conditions of Use",
    chipColor: "#f2a413",
    img: "/safety-conditions-use.png",
    alt: "Official Cool Runnings park poster — Conditions of Use / Gebruiksvoorwaardes with rider diagrams: keep 25 m behind, the smaller rider must sit in front, end of track, faster and braking poses",
    caption: "The full Conditions of Use poster at the park entrance — English & Afrikaans.",
  },
  {
    id: "public-notice",
    chip: "Public Notice",
    chipColor: "#4fb8cf",
    img: "/safety-public-notice.png",
    alt: "Official red Public Notice poster — Public Notice / Publieke Kennisgewing: the eight rules for using the installation safely",
    caption: "The Public Notice as displayed at the park — English & Afrikaans.",
  },
  {
    id: "wiegand-lifter",
    chip: "Lifter",
    chipColor: "#7ca836",
    img: "/safety-wiegand-lifter.png",
    alt: "Official yellow poster — Conditions of Use for the Lifter / Voorwaarders vir Gebruik vir die Lifter",
    caption: "Conditions of use for the Lifter — English & Afrikaans.",
  },
  {
    id: "disclaimer",
    chip: "Disclaimer",
    chipColor: "#d63a2f",
    img: "/safety-disclaimer.webp",
    alt: "Official yellow poster — Disclaimer / Skadeloosstelling",
    caption: "Our disclaimer — English & Afrikaans.",
  },
  {
    id: "terms",
    chip: "Terms & Conditions",
    chipColor: "#d63a2f",
    img: "/safety-terms-conditions.png",
    alt: "Official red and yellow poster — Terms and Conditions / Bepalings en Voorwaardes, twelve numbered clauses side by side",
    caption: "The full Terms & Conditions — English & Afrikaans.",
  },
];

function SignSection({ sign }) {
  return (
    <section className="mx-auto max-w-3xl px-4 py-10 sm:px-6" data-testid={`safety-section-${sign.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <span className="rounded-lg px-4 py-2 font-display text-lg text-white" style={{ backgroundColor: sign.chipColor }}>
          {sign.chip}
        </span>
        <span className="text-xs font-extrabold uppercase tracking-widest text-[#8a7b5e]">
          English &amp; Afrikaans · tap a poster to view full size
        </span>
      </div>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.7 }}
        className="mt-5 overflow-hidden rounded-3xl border-2 bg-[#0a0a0b] shadow-[0_20px_60px_rgba(36,28,13,0.25)]"
        style={{ borderColor: `${sign.chipColor}66` }}
        data-testid={`safety-${sign.id}-card`}
      >
        <a href={sign.img} target="_blank" rel="noopener noreferrer" title="View full size">
          <img
            src={sign.img}
            alt={sign.alt}
            className="w-full transition-transform duration-500 hover:scale-[1.02]"
            loading="lazy"
            data-testid={`safety-${sign.id}-image`}
          />
        </a>
      </motion.div>
      <p className="mt-4 text-center text-xs font-extrabold uppercase tracking-widest text-[#8a7b5e]">{sign.caption}</p>
    </section>
  );
}

export default function HealthSafetyPage() {
  useSeo({
    title: "Health & Safety | Cool Runnings Cape Town",
    description:
      "Track rules, height checks and ride safety at Cool Runnings — the official park signage: Conditions of Use, Public Notice, lifter rules, disclaimer and full terms in English & Afrikaans.",
    keywords:
      "toboggan safety rules, Cool Runnings safety, toboggan height restrictions, family attraction safety Cape Town, ride safety Cape Town",
    image: "/og/safety.jpg",
  });
  return (
    <>
      <PageHeader
        goodgirlTitle
        pageName="Health & Safety"
        eyebrow="Rules"
        title="HEALTH & SAFETY"
        subline="The official signage you'll find at the park — Conditions of Use, public notices, the lifter rules, our disclaimer and full terms, in English and Afrikaans."
        image="/park-track.jpg"
      />

      {SIGNS.map((sign) => (
        <SignSection key={sign.id} sign={sign} />
      ))}

      <section className="mx-auto max-w-3xl px-4 pb-14 pt-4 sm:px-6" data-testid="safety-section-qr">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
          className="overflow-hidden rounded-3xl border-2 border-[#f8b818]/50 bg-[#0a0a0b] shadow-[0_20px_60px_rgba(36,28,13,0.25)]"
          data-testid="safety-qr-card"
        >
          <a href="/language-qr-poster.jpg" target="_blank" rel="noopener noreferrer" title="View full size">
            <img
              src="/language-qr-poster.jpg"
              alt="Conditions of Use in your language — QR codes for English, German, Dutch, French, Indian, Chinese, Spanish, Portuguese, Italian and Xhosa versions"
              className="mx-auto w-full max-w-md transition-transform duration-500 hover:scale-[1.02]"
              loading="lazy"
              data-testid="safety-qr-image"
            />
          </a>
        </motion.div>
        <p className="mt-4 text-center text-xs font-extrabold uppercase tracking-widest text-[#8a7b5e]">
          Scan a code to read the Conditions of Use in your language — English, German, Dutch, French, Indian,
          Chinese, Spanish, Portuguese, Italian or Xhosa.
        </p>
      </section>

      <section className="mx-auto max-w-3xl px-4 pb-14 pt-4 sm:px-6" data-testid="safety-section-warning">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
          className="overflow-hidden rounded-3xl border-2 border-[#d63a2f]/60 bg-[#0a0a0b] shadow-[0_20px_60px_rgba(214,58,47,0.25)]"
          data-testid="safety-warning-card"
        >
          <a href="/warning-notice.jpg" target="_blank" rel="noopener noreferrer" title="View full size">
            <img
              src="/warning-notice.jpg"
              alt="Warning notice — This is a sports installation. Riders tobogganing too fast or too close to the toboggan in front endanger everyone. You are personally responsible for all the consequences of reckless riding!"
              className="w-full transition-transform duration-500 hover:scale-[1.02]"
              data-testid="safety-warning-image"
            />
          </a>
        </motion.div>
        <p className="mt-4 text-center text-xs font-extrabold uppercase tracking-widest text-[#8a7b5e]">
          The warning notice at the track entrance — ride with care, every sled matters.
        </p>
      </section>
    </>
  );
}
